// Browser-safe CommonJS: the frontend uses a byte-for-byte copy of this module.
const UNIT_OPTIONS = [
  { value: 'kg', label: 'Kilogram (kg)' },
  { value: 'g', label: 'Gram (g)' },
  { value: 'l', label: 'Litre (l)' },
  { value: 'ml', label: 'Millilitre (ml)' },
  { value: 'dozen', label: 'Dozen' },
  { value: 'pc', label: 'Piece (pc)' },
  { value: 'box', label: 'Box' },
  { value: 'pack', label: 'Pack' },
  { value: 'bottle', label: 'Bottle' }
];

const aliases = {
  kg: 'kg', kilogram: 'kg', kilograms: 'kg', kilo: 'kg', kilos: 'kg',
  g: 'g', gram: 'g', grams: 'g', gm: 'g', gms: 'g',
  l: 'l', litre: 'l', litres: 'l', liter: 'l', liters: 'l', ltr: 'l', ltrs: 'l',
  ml: 'ml', millilitre: 'ml', millilitres: 'ml', milliliter: 'ml', milliliters: 'ml',
  dozen: 'dozen', dozens: 'dozen', dz: 'dozen',
  pc: 'pc', pcs: 'pc', piece: 'pc', pieces: 'pc',
  box: 'box', boxes: 'box', pack: 'pack', packs: 'pack', packet: 'pack', packets: 'pack',
  bottle: 'bottle', bottles: 'bottle'
};

const dimensions = { kg: 'mass', g: 'mass', l: 'volume', ml: 'volume', dozen: 'count', pc: 'count' };
const standardFactors = { 'kg:g': 1000, 'g:kg': 0.001, 'l:ml': 1000, 'ml:l': 0.001, 'dozen:pc': 12, 'pc:dozen': 1 / 12 };
const unitPattern = /^[a-z][a-z0-9_-]*$/;
const factorPattern = /^(?:\d+)(?:\.\d{1,6})?$/;
const expressionPattern = /^\s*1\s*([a-z][a-z0-9_-]*)\s*=\s*(\d+(?:\.\d{1,6})?)\s*([a-z][a-z0-9_-]*)\s*$/i;

function normalizeUnit(value) {
  const unit = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return aliases[unit] || unit;
}

function suggestFactor(purchaseUnit, usageUnit) {
  const purchase = normalizeUnit(purchaseUnit);
  const usage = normalizeUnit(usageUnit);
  if (!purchase || !usage) return '';
  if (purchase === usage) return '1';
  const factor = standardFactors[`${purchase}:${usage}`];
  if (!factor || !factorPattern.test(String(factor))) return '';
  return String(factor);
}

function makeConversion({ purchaseUnit, usageUnit, factor } = {}) {
  const purchase = normalizeUnit(purchaseUnit);
  const usage = normalizeUnit(usageUnit);
  if (!unitPattern.test(purchase) || !unitPattern.test(usage)) throw new Error('Valid purchase and usage units are required');

  const factorText = String(factor).trim();
  if (!factorPattern.test(factorText)) throw new Error('Conversion factor must have at most six decimal places');
  const numericFactor = Number(factorText);
  if (!Number.isFinite(numericFactor) || numericFactor <= 0 || numericFactor > Number.MAX_SAFE_INTEGER) {
    throw new Error('Conversion factor must be positive, finite and safely representable');
  }
  const [integerPart, fractionalPart = ''] = factorText.split('.');
  const normalizedInteger = integerPart.replace(/^0+(?=\d)/, '');
  const normalizedFraction = fractionalPart.replace(/0+$/, '');
  const enteredDecimal = normalizedFraction ? `${normalizedInteger}.${normalizedFraction}` : normalizedInteger;
  if (String(numericFactor) !== enteredDecimal) {
    throw new Error('Conversion factor precision is not safely representable');
  }

  const purchaseDimension = dimensions[purchase];
  const usageDimension = dimensions[usage];
  if (purchaseDimension && usageDimension && purchaseDimension !== usageDimension) {
    throw new Error('Incompatible unit dimensions');
  }
  if (purchase === usage && numericFactor !== 1) throw new Error('Identical units must have factor 1');
  const standard = standardFactors[`${purchase}:${usage}`];
  if (standard && numericFactor !== standard) throw new Error('Conversion factor does not match standard units');

  const canonicalFactor = String(numericFactor);
  return {
    purchaseUnit: purchase,
    usageUnit: usage,
    factor: numericFactor,
    equivalentNo: `1 ${purchase} = ${canonicalFactor} ${usage}`
  };
}

function parseConversion({ purchaseUnit, usageUnit, equivalentNo } = {}) {
  const match = typeof equivalentNo === 'string' ? equivalentNo.match(expressionPattern) : null;
  if (!match) throw new Error('Conversion must look like 1 kg = 1000 g');
  if (normalizeUnit(match[1]) !== normalizeUnit(purchaseUnit) || normalizeUnit(match[3]) !== normalizeUnit(usageUnit)) {
    throw new Error('Conversion units do not match item units');
  }
  return makeConversion({ purchaseUnit, usageUnit, factor: match[2] });
}

module.exports = { UNIT_OPTIONS, normalizeUnit, suggestFactor, parseConversion, makeConversion };
